/**
 * Mémorise le prénom de l'invité et le dernier moment choisi entre deux
 * visites. `localStorage` peut lever en navigation privée : accès défensif.
 */
import { defineStore } from "pinia";
import { ref } from "vue";

import type { MomentId } from "@/types/album";

const NAME_STORAGE_KEY = "sq-album:guest-name";
const MOMENT_STORAGE_KEY = "sq-album:last-moment";
const KNOWN_MOMENTS: readonly MomentId[] = ["ceremonie", "cocktail", "diner", "soiree"];

function isMomentId(value: string | null): value is MomentId {
  return value !== null && (KNOWN_MOMENTS as readonly string[]).includes(value);
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Navigation privée ou quota dépassé : la préférence ne survit pas à la session.
  }
}

export const useGuestIdentityStore = defineStore("guestIdentity", () => {
  const name = ref(readStorage(NAME_STORAGE_KEY) ?? "");
  const storedMoment = readStorage(MOMENT_STORAGE_KEY);
  /**
   * `null` tant que l'invité n'a rien choisi : aucun moment n'est présélectionné
   * à sa place. Un moment déjà choisi lors d'une visite précédente est en
   * revanche repris, comme le prénom.
   */
  const lastMoment = ref<MomentId | null>(isMomentId(storedMoment) ? storedMoment : null);

  function setName(value: string): void {
    name.value = value;
    writeStorage(NAME_STORAGE_KEY, value);
  }

  function setLastMoment(value: MomentId): void {
    lastMoment.value = value;
    writeStorage(MOMENT_STORAGE_KEY, value);
  }

  return { name, lastMoment, setName, setLastMoment };
});
